import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Entity} from '../../entity/entity';
import {OrganizationAddress} from '../../organization/organization-address';
import {PersonLogin} from '../../person/person-login';
import {EntityService} from '../../services/entity-service';
import {OrderService} from '../../services/order-service';
import {OrganizationAddressService} from '../../services/organization-address-service';
import {QuoteService} from '../../services/quote-service';
import {StringUtils} from '../../utils/string-utils';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import {AddressUtils} from '../../utils/address-utils';
import {ValidatedAddressInfo} from '../../model/validated-address-info';
import {AddressAlternativeDialogComponent} from '../address-alternative-dialog/address-alternative-dialog.component';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { OrgAddressDetailComponent } from '../../organization/org-address-detail.component';
import { OrgAddressGridComponent } from '../../organization/org-address-grid.component';


@Component({
    selector: 'address-selection-dialog',
    templateUrl: './address-selection-dialog.component.html',
    styleUrls: ['./address-selection-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [OrgAddressGridComponent, OrgAddressDetailComponent, MatProgressSpinner, MatDialogActions, FormsModule, MatButton]
})
export class AddressSelectionDialogComponent {
  private readonly addressService = inject(OrganizationAddressService);
  private readonly quoteService = inject(QuoteService);
  private readonly orderService = inject(OrderService);
  private readonly entityService = inject(EntityService);
  private dialogRef = inject<MatDialogRef<AddressSelectionDialogComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject(MAT_DIALOG_DATA);


  readonly entity = signal<Entity>(undefined!);
  private personLogin: PersonLogin;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly selectedAddress = signal<OrganizationAddress>(undefined!);
  readonly addressList = signal<OrganizationAddress[]>(undefined!);
  readonly addAddress = signal<boolean>(undefined!);
  public isNew: boolean = false;
  public readOnly: boolean = false;

  constructor
  () {
    const data = this.data;

    this.readOnly = data.readOnly as boolean;
    this.entity.set(data.entity as Entity);
    this.personLogin = data.personLogin as PersonLogin;
    this.addressService.getOrganizationAddresses(this.entity().repOfficeId).subscribe({
      next: results => this.addressList.set(results),
      error: error => this.handleError(error)
    });
    if (this.entity().overrideShipToAddressLine1) {
      this.selectedAddress.set(new OrganizationAddress());
      this.selectedAddress().typeCode = "SHIPTO";
      this.selectedAddress().attentionLine = this.entity().overrideShipToAttentionLine;
      this.selectedAddress().addressLine1 = this.entity().overrideShipToAddressLine1;
      this.selectedAddress().addressLine2 = this.entity().overrideShipToAddressLine2;
      this.selectedAddress().addressLine3 = this.entity().overrideShipToAddressLine3;
      this.selectedAddress().cityName = this.entity().overrideShipToCityName;
      this.selectedAddress().stateCode = this.entity().overrideShipToStateCode;
      this.selectedAddress().postalCode = this.entity().overrideShipToPostalCode;
      this.selectedAddress().countryCode = this.entity().overrideShipToCountryCode;
      this.selectedAddress().orgUnitId = this.entity().repOfficeId;
    }
  }

  public selectAddress(address: OrganizationAddress): void {
    this.errorMessage.set(undefined!);
    this.selectedAddress.set(address);
    this.addAddress.set(false);
    this.isNew = false;
  }

  public clearSelectedAddress(): void {
    this.selectAddress(undefined!);
  }

  public newAddress(): void {
    this.errorMessage.set(undefined!);
    this.selectedAddress.set(new OrganizationAddress());
    this.selectedAddress().typeCode = "SHIPTO";
    this.selectedAddress().orgUnitId = this.entity().repOfficeId;
    this.selectedAddress().createUser = this.personLogin.person.userLogin;
    this.selectedAddress().updateUser = this.personLogin.person.userLogin;
    this.addAddress.set(true);
    this.isNew = true;
  }

  public saveAndClose(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    if (AddressUtils.isGeneralDeliverySpecified(this.selectedAddress())) {
      this.saveAddressAndEntity();
    } else {
      this.validateAndSaveAddressAndEntity();
    }
  }

  private validateAndSaveAddressAndEntity(): void {
    this.addressService.validateAddress(this.selectedAddress()).subscribe({
      next: resolution => {
        if (!resolution.validAddress || AddressUtils.validatedAddressIsDifferent(resolution.validatedAddresses, this.selectedAddress())) {
          const validatedAddress: ValidatedAddressInfo = resolution.validAddress
            ? resolution.validatedAddresses[0]
            : AddressUtils.injectGeneralDeliveryLine(this.selectedAddress())!;
          const suggestionDialog = this.dialog.open(AddressAlternativeDialogComponent, {
            width: '1064px',
            height: '520px',
            data: {resolution: resolution, enteredAddress: this.selectedAddress(), validatedAddress: validatedAddress},
          });
          suggestionDialog.afterClosed().subscribe({
            next: result => {
              if (result) {
                this.selectedAddress.set(AddressUtils.updateAddressWithValidated(this.selectedAddress(), validatedAddress));
                this.selectedAddress().createUser = this.personLogin.person.userLogin;
                this.selectedAddress().updateUser = this.personLogin.person.userLogin;
                this.addressService.save(this.selectedAddress()).subscribe(() => {
                  this.addAddress.set(false);
                  this.saveAddressAndEntity();
                }, error => this.handleError(error));
              } else {
                if (resolution.validAddress) {
                  this.saveAddressAndEntity();
                } else {
                  this.isBusy.set(false);
                }
              }
            },
            error: error => this.handleError(error)
          });
        } else {
          this.saveAddressAndEntity();
        }
      },
      error: error => this.handleError(error)
    });
  }

  private saveAddressAndEntity(): void {
    this.entityService.getEntityByKey(this.entity().id).subscribe({
      next: (result: Entity) => {
        result.overrideShipToAttentionLine = this.selectedAddress().attentionLine;
        result.overrideShipToAddressLine1 = this.selectedAddress().addressLine1;
        result.overrideShipToAddressLine2 = this.selectedAddress().addressLine2;
        result.overrideShipToAddressLine3 = this.selectedAddress().addressLine3;
        result.overrideShipToCityName = this.selectedAddress().cityName;
        result.overrideShipToStateCode = this.selectedAddress().stateCode;
        result.overrideShipToPostalCode = this.selectedAddress().postalCode;
        result.overrideShipToCountryCode = this.selectedAddress().countryCode;
        result.deliveryCountryCode = this.selectedAddress().countryCode;
        this.entity.set(result);
        if (this.addAddress()) {
          this.addressService.save(this.selectedAddress()).subscribe({
            next: () => this.saveEntity(),
            error: error => this.handleError(error)
          });
        } else {
          this.saveEntity();
        }
      }, error: error => this.handleError(error)
    });
  }

  private saveEntity(): void {
    if (this.entity().id.entityName === 'QUOTE') {
      this.quoteService.save(this.entity()).subscribe({
        next: result => this.dialogRef.close(result.updated),
        error: error => this.handleError(error)
      });
    } else if (this.entity().id.entityName === 'CONTRACT') {
      this.orderService.save(this.entity()).subscribe({
        next: result => this.dialogRef.close(result.updated),
        error: error => this.handleError(error)
      })
    }
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

}

